/**
 * Skills Management Page
 * Card-based layout matching the Data Bundles design
 */

import React, { useState, useMemo } from 'react';
import { formatRelativeTime } from '@/utils/relativeTime';
import { message, Spin } from 'antd';
import { Sidebar } from '@/components/layout/Sidebar';
import { useLanguage } from '../../../hooks/useLanguage';
import { useAuth } from '../../../hooks/useAuth';
import { useCachedFetch, invalidateCachedFetch } from '@/hooks/useCachedFetch';
import { SkillsApi } from '../services/skillsApi';
import { sharingService } from '@services/sharingService';
import { SkillModal } from '../components/SkillModal';
import { SkillViewModal } from '../components/SkillViewModal';
import { ShareModal } from '@/components/sharing/ShareModal';
import type { Skill } from '../types';
import checkIcon from '@/brand-assets/m2/General Icons/woman with paper3.png';
import './SkillsManagementPage.css';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return formatRelativeTime(date);
}

type FilterTab = 'all' | 'mine' | 'shared';

export const SkillsManagementPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useLanguage();
  const { user, isAdmin, isGroupAdmin, isSuperAdmin } = useAuth();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [shareSkillId, setShareSkillId] = useState('');
  const [shareSkillName, setShareSkillName] = useState('');
  const [shareEntityIds, setShareEntityIds] = useState<string[]>([]);
  const [viewSkill, setViewSkill] = useState<Skill | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  // Who shared it. "Shared with me" alone does not say whether a colleague
  // picked you out or the whole organization got it, and it gives you nobody
  // to ask about the skill.
  const [sharedBy, setSharedBy] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const canShare = isAdmin || isGroupAdmin || isSuperAdmin;

  // SWR — instant paint on revisit. First visit shows loading state;
  // every subsequent visit in the same session paints the cached
  // list synchronously and refreshes silently in the background.
  const skillsKey = useMemo(() => ['skills', user?.user_id, searchTerm] as const, [user?.user_id, searchTerm]);
  const { data: skillsData, loading, refetch: reloadSkills } = useCachedFetch<Skill[]>(
    skillsKey,
    async () => {
      const data = await SkillsApi.listSkills({ search: searchTerm || undefined });
      return data.skills;
    },
  );
  const sharedSkillsKey = useMemo(() => ['shared-skills', user?.user_id] as const, [user?.user_id]);
  const { data: sharedSkillsData, loading: sharedLoading, refetch: reloadShared } = useCachedFetch<Skill[]>(
    sharedSkillsKey,
    async () => {
      const data = await sharingService.getSharedEntities('skills');
      return (data as Skill[]) || [];
    },
  );
  // Default to [] so downstream code never sees null. (useCachedFetch
  // returns null during the very first fetch when no cache exists.)
  const skills = skillsData ?? [];
  const sharedSkills = sharedSkillsData ?? [];

  // Convenience wrappers for the call sites that previously used
  // `loadSkills()` / `loadSharedSkills()` — invalidate cache too.
  const loadSkills = () => { invalidateCachedFetch(skillsKey as unknown as readonly (string|number|null|undefined)[]); return reloadSkills(); };
  // Re-fetcher kept for external invalidation flows (e.g. after a share).
  void invalidateCachedFetch;
  void reloadShared;

  const handleShare = (skill: Skill) => {
    setShareSkillId(skill.skill_id);
    setShareSkillName(skill.skill_name);
    setShareEntityIds([]);
    setShareModalVisible(true);
  };

  const handleBulkShare = () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setShareSkillId(ids[0]);
    setShareSkillName(`${ids.length} skills selected`);
    setShareEntityIds(ids);
    setShareModalVisible(true);
  };

  const handleShareModalClose = () => {
    setShareModalVisible(false);
    setShareEntityIds([]);
    setSelectedIds(new Set());
  };

  const handleCreate = () => {
    setSelectedSkill(undefined);
    setModalVisible(true);
  };

  const handleEdit = (skill: Skill) => {
    setSelectedSkill(skill);
    setModalVisible(true);
  };

  const handleDelete = async (skillId: string) => {
    if (!window.confirm(t('skills.confirmDelete'))) {
      return;
    }
    try {
      await SkillsApi.deleteSkill(skillId);
      message.success(t('skills.deleteSuccess'));
      loadSkills();
    } catch (error: any) {
      message.error(t('skills.deleteError'));
      console.error('Error deleting skill:', error);
    }
  };

  const handleToggleActive = async (skill: Skill) => {
    try {
      await SkillsApi.updateSkill(skill.skill_id, {
        is_active: !skill.is_active,
      });
      message.success(skill.is_active ? t('skills.skillDeactivated') : t('skills.skillActivated'));
      loadSkills();
    } catch (error) {
      message.error(t('skills.updateError'));
      console.error('Error updating skill:', error);
    }
  };

  const handleModalSave = () => {
    setModalVisible(false);
    loadSkills();
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setSelectedSkill(undefined);
  };

  const handleFilterChange = (filter: FilterTab) => {
    setActiveFilter(filter);
    setSelectedIds(new Set());
  };

  const handleSelectToggle = (skillId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(skillId)) {
        next.delete(skillId);
      } else {
        next.add(skillId);
      }
      return next;
    });
  };

  const handleCancelSelection = () => {
    setSelectedIds(new Set());
  };

  const activeSkills = skills.filter(s => s.is_active);
  const inactiveSkills = skills.filter(s => !s.is_active);

  // Everyone who has shared a skill with you, each listed once with a count.
  const sharers = useMemo(() => {
    const counts = new Map<string, { id: string; name: string; count: number }>();
    sharedSkills.forEach((s) => {
      const id = s.shared_by_user_id || 'unknown';
      const name = s.shared_by_name || 'Unknown';
      const row = counts.get(id) || { id, name, count: 0 };
      row.count += 1;
      counts.set(id, row);
    });
    return [...counts.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [sharedSkills]);

  // Client-side search + sharer filter for shared skills
  const filteredSharedSkills = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return sharedSkills.filter((s) => {
      if (sharedBy !== 'all' && (s.shared_by_user_id || 'unknown') !== sharedBy) return false;
      if (!term) return true;
      return (
        s.skill_name.toLowerCase().includes(term) ||
        (s.skill_description && s.skill_description.toLowerCase().includes(term))
      );
    });
  }, [sharedSkills, searchTerm, sharedBy]);

  const totalAll = skills.length + filteredSharedSkills.length;
  const totalMine = skills.length;
  const totalShared = filteredSharedSkills.length;

  // Determine if we have a completely empty state (no skills at all, unfiltered)
  const isCompletelyEmpty = skills.length === 0 && sharedSkills.length === 0;

  const skillsInner = (
    <>
          {/* Header */}
          <div className="simple-chat-header">
            <h1 dir="auto" className="bot-title">{t('skills.pageTitle')}</h1>
            <p className="text-muted">{t('skills.pageSubtitle')}</p>
          </div>

          {/* Search + Create Row */}
          <div className="skills-controls">
            <div className="skills-search-box">
              <svg className="skills-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input dir="auto"
                type="text"
                className="skills-search-input"
                placeholder={t('admin.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="skills-create-btn" onClick={handleCreate}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
              {t('skills.create')}
            </button>
          </div>

          {/* Filter Pills */}
          <div className="skills-filter-pills">
            <button
              className={`skills-filter-pill ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterChange('all')}
            >
              {t('common.filterAll')} <span className="skills-filter-pill-count">{totalAll}</span>
            </button>
            <button
              className={`skills-filter-pill ${activeFilter === 'mine' ? 'active' : ''}`}
              onClick={() => handleFilterChange('mine')}
            >
              {t('common.filterMine')} <span className="skills-filter-pill-count">{totalMine}</span>
            </button>
            <button
              className={`skills-filter-pill ${activeFilter === 'shared' ? 'active' : ''}`}
              onClick={() => handleFilterChange('shared')}
            >
              {t('common.filterShared')} <span className="skills-filter-pill-count">{totalShared}</span>
            </button>

            {/* Only where it means something: on "Mine" there is nobody to
                filter by, and with a single sharer the control is noise. */}
            {activeFilter !== 'mine' && sharers.length > 1 && (
              <select
                className="skills-sharer-filter"
                value={sharedBy}
                onChange={(e) => setSharedBy(e.target.value)}
                aria-label={t('skills.sharedByFilterLabel')}
              >
                <option value="all">{t('skills.sharedByAnyone')}</option>
                {sharers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.count})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Skills List */}
          <div className="skills-list-container">
            {(loading || sharedLoading) ? (
              <div className="skills-loading">
                <Spin size="large" />
              </div>
            ) : isCompletelyEmpty ? (
              /* Full empty state: no own skills AND no shared skills */
              <div className="skills-empty">
                <img src={checkIcon} alt="" className="skills-empty-icon" />
                <h3>{t('skills.noSkillsYet')}</h3>
                <p>{t('skills.noSkillsYetHint')}</p>
                <button className="skills-create-btn" onClick={handleCreate}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  {t('skills.create')}
                </button>
              </div>
            ) : (
              <>
                {/* "Mine" or "All" filter: show own skills */}
                {(activeFilter === 'all' || activeFilter === 'mine') && (
                  <>
                    {skills.length === 0 && activeFilter === 'mine' ? (
                      /* Compact inline empty only on "Mine" tab */
                      <div className="skills-inline-empty">
                        <span>{t('skills.youHaveNoSkills')}</span>
                        <button className="skills-inline-create-link" onClick={handleCreate}>
                          {t('skills.createOne')}
                        </button>
                      </div>
                    ) : skills.length > 0 ? (
                      <>
                        {/* Active Skills Section */}
                        {activeSkills.length > 0 && (
                          <div className="skills-section">
                            <div className="skills-section-header">
                              <span className="skills-section-dot active" />
                              {t('skills.activeSkills')}
                              <span className="skills-section-count">{activeSkills.length}</span>
                            </div>
                            {activeSkills.map(skill => (
                              <SkillCard
                                key={skill.skill_id}
                                skill={skill}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onToggleActive={handleToggleActive}
                                onShare={handleShare}
                                onView={(s) => setViewSkill(s)}
                                canShare={canShare}
                                selectable={canShare}
                                selected={selectedIds.has(skill.skill_id)}
                                onSelectToggle={handleSelectToggle}
                              />
                            ))}
                          </div>
                        )}

                        {/* Inactive Skills Section */}
                        {inactiveSkills.length > 0 && (
                          <div className="skills-section">
                            <div className="skills-section-header">
                              <span className="skills-section-dot inactive" />
                              {t('skills.inactiveSkills')}
                              <span className="skills-section-count">{inactiveSkills.length}</span>
                            </div>
                            {inactiveSkills.map(skill => (
                              <SkillCard
                                key={skill.skill_id}
                                skill={skill}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onToggleActive={handleToggleActive}
                                onShare={handleShare}
                                onView={(s) => setViewSkill(s)}
                                canShare={canShare}
                                selectable={canShare}
                                selected={selectedIds.has(skill.skill_id)}
                                onSelectToggle={handleSelectToggle}
                              />
                            ))}
                          </div>
                        )}
                      </>
                    ) : null}
                  </>
                )}

                {/* "Shared" or "All" filter: show shared skills */}
                {(activeFilter === 'all' || activeFilter === 'shared') && (
                  <>
                    {filteredSharedSkills.length === 0 ? (
                      activeFilter === 'shared' && (
                        <div className="skills-inline-empty">
                          <span>{t('skills.noSkillsSharedWithYou')}</span>
                        </div>
                      )
                    ) : (
                      <div className="skills-section">
                        {activeFilter === 'all' && (
                          <div className="skills-section-header">
                            <span className="skills-section-dot shared" />
                            {t('skills.sharedWithMe')}
                            <span className="skills-section-count">{filteredSharedSkills.length}</span>
                          </div>
                        )}
                        {filteredSharedSkills.map(skill => (
                          <SkillCard
                            key={`shared-${skill.skill_id}`}
                            skill={skill}
                            onEdit={() => {}}
                            onDelete={() => {}}
                            onToggleActive={() => {}}
                            onShare={skill.can_reshare ? handleShare : undefined}
                            onView={(s) => setViewSkill(s)}
                            canShare={!!skill.can_reshare}
                            isShared
                            selectable={!!skill.can_reshare && canShare}
                            selected={selectedIds.has(skill.skill_id)}
                            onSelectToggle={handleSelectToggle}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Bulk Selection Action Bar */}
          {selectedIds.size > 0 && (
            <div className="skills-bulk-bar">
              <span className="skills-bulk-count">{t('common.nSelected', { count: selectedIds.size })}</span>
              <div className="skills-bulk-actions">
                <button className="skills-bulk-btn skills-bulk-share" onClick={handleBulkShare}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3"/>
                    <circle cx="6" cy="12" r="3"/>
                    <circle cx="18" cy="19" r="3"/>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                  </svg>
                  {t('sharing.title')}
                </button>
                <button className="skills-bulk-btn skills-bulk-cancel" onClick={handleCancelSelection}>
                  {t('vault.cancel')}
                </button>
              </div>
            </div>
          )}
    </>
  );

  const skillsModals = (
    <>
      <SkillModal
        visible={modalVisible}
        skill={selectedSkill}
        onSave={handleModalSave}
        onCancel={handleModalCancel}
      />

      <ShareModal
        isOpen={shareModalVisible}
        onClose={handleShareModalClose}
        entityType="skills"
        entityId={shareSkillId}
        entityName={shareSkillName}
        entityIds={shareEntityIds.length > 0 ? shareEntityIds : undefined}
      />

      <SkillViewModal
        visible={!!viewSkill}
        skill={viewSkill}
        onClose={() => setViewSkill(null)}
      />
    </>
  );

  // Embedded mode (rendered as a tab inside the Agents page): no Sidebar / page
  // chrome — the body sits directly in the host's content column.
  if (embedded) {
    return <>{skillsInner}{skillsModals}</>;
  }

  return (
    <div className="admin-container">
      <Sidebar />
      <div className="main-container">
        <main className="main-content">{skillsInner}</main>
      </div>
      {skillsModals}
    </div>
  );
};

/* ── Skill Card Component ── */

interface SkillCardProps {
  skill: Skill;
  onEdit: (skill: Skill) => void;
  onDelete: (skillId: string) => void;
  onToggleActive: (skill: Skill) => void;
  onShare?: (skill: Skill) => void;
  onView?: (skill: Skill) => void;
  canShare?: boolean;
  isShared?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onSelectToggle?: (skillId: string) => void;
}

const SkillCard: React.FC<SkillCardProps> = ({ skill, onEdit, onDelete, onToggleActive, onShare, onView, canShare, isShared, selectable, selected, onSelectToggle }) => {
  const { t } = useLanguage();
  const fileCount = skill.file_count || 0;
  const bundleCount = skill.data_bundle_ids?.length || 0;
  const agentCount = skill.agent_count || 0;

  return (
    <div className={`skill-card-row ${!skill.is_active ? 'skill-card-inactive' : ''} ${selected ? 'skill-card-selected' : ''}`}>
      {selectable && (
        <label className="skill-card-checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            className="skill-card-checkbox"
            checked={!!selected}
            onChange={() => onSelectToggle?.(skill.skill_id)}
          />
          <span className="skill-card-checkbox-custom" />
        </label>
      )}

      <img src={checkIcon} alt="" className="skill-card-icon" />

      <div className="skill-card-info">
        <div className="skill-card-name" dir="auto">{skill.skill_name}</div>
        {skill.skill_description && (
          <div className="skill-card-description" dir="auto">{skill.skill_description}</div>
        )}
        <div className="skill-card-meta">
          <span className="skill-card-date">
            {formatDate(skill.created_at)}
          </span>
          {isShared && (
            <span className="skill-card-label skill-card-shared-label">
              {/* Naming the sharer is the point. "Shared" alone leaves a person
                  unable to tell a colleague's skill from one the whole
                  organization received, or to know who to ask about it. */}
              {skill.shared_by_name
                ? (skill.shared_via === 'tenant'
                    ? t('skills.sharedWithEveryoneBy', { name: skill.shared_by_name })
                    : t('skills.sharedByPerson', { name: skill.shared_by_name }))
                : t('skills.sharedLabel')}
            </span>
          )}
        </div>
      </div>

      <div className="skill-card-labels">
        {fileCount > 0 && (
          <span className="skill-card-label skill-card-label-files">
            {t('counts.files', { count: fileCount })}
          </span>
        )}
        {bundleCount > 0 && (
          <span className="skill-card-label skill-card-label-bundles">
            {t('counts.bundles', { count: bundleCount })}
          </span>
        )}
        {agentCount > 0 && (
          <span className="skill-card-label skill-card-label-agents">
            {t('counts.agents', { count: agentCount })}
          </span>
        )}
      </div>

      <div className="skill-card-status">
        <span className={`skill-card-badge ${skill.is_active ? 'active' : 'inactive'}`}>
          <span className="skill-card-badge-dot" />
          {skill.is_active ? t('common.active') : t('common.inactive')}
        </span>
      </div>

      <div className="skill-card-actions">
        {onView && (
          <button
            className="skill-action-btn"
            onClick={() => onView(skill)}
            title={t('skills.viewDetails')}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        )}
        {canShare && onShare && (
          <button
            className="skill-action-btn"
            onClick={() => onShare(skill)}
            title={t('skills.shareSkill')}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </button>
        )}
        {!isShared && (
          <>
            <button
              className="skill-action-btn"
              onClick={() => onEdit(skill)}
              title={t('skills.editSkillAction')}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              className="skill-action-btn"
              onClick={() => onToggleActive(skill)}
              title={skill.is_active ? t('common.deactivate') : t('common.activate')}
            >
              {skill.is_active ? (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                  <line x1="12" y1="2" x2="12" y2="12" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              )}
            </button>
            <button
              className="skill-action-btn skill-action-danger"
              onClick={() => onDelete(skill.skill_id)}
              title={t('skills.deleteSkillAction')}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
};
